import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Fade,
  Slide,
  Button,
} from '@mui/material';
import {
  School,
  Assignment,
  Star,
  TrackChanges,
  VerifiedUser,
  Analytics,
  Business,
  Psychology,
  ContactSupport,
  Work,
  Person,
  Construction,
} from '@mui/icons-material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineContent,
  TimelineConnector,
  TimelineDot,
} from '@mui/lab';
import PublicLayout from '@/layouts/PublicLayout';
import { keyframes } from '@mui/system';
import dreamzConnectLogo from '@/assets/logos/dreamzconnect.svg';
import { getFeatureFlags } from '@/config/featureFlags';
import { motion } from 'framer-motion';

// Define the tab values
type TabValue = 'students' | 'teachers' | 'employers';

// Content configuration
const studentSteps = [
  {
    label: 'Smart Matching',
    description: 'Our AI-powered platform matches you with opportunities that align with your interests and career goals, providing personalized recommendations.',
    icon: Psychology,
  },
  {
    label: 'Track & Analyze',
    description: 'Track your volunteer hours and get AI-driven insights about your skills development and impact metrics.',
    icon: Analytics,
  },
  {
    label: 'Build Portfolio',
    description: 'Showcase your achievements with automated skill assessments and impact analytics for college applications.',
    icon: Star,
  },
];

const teacherSteps = [
  {
    label: 'Intelligent Tracking',
    description: 'A platform for accurately monitoring student volunteer hours, with reporting features for up-to-date progress tracking.',
    icon: TrackChanges,
  },
  {
    label: 'Streamline Administration',
    description: 'Digital verification eliminates paperwork, saving valuable teacher time so they can spend more time teaching.',
    icon: VerifiedUser,
  },
  {
    label: 'Data Insights',
    description: 'Advanced analytics dashboard showing student engagement patterns and skill development trends.',
    icon: Analytics,
  },
];

const employerSteps = [
  {
    label: 'Smart Posting',
    description: 'AI-assisted opportunity creation with smart matching to reach the most suitable student volunteers.',
    icon: Psychology,
  },
  {
    label: 'Talent Analytics',
    description: 'Access detailed analytics about volunteer engagement and impact measurement.',
    icon: Analytics,
  },
  {
    label: 'Automated Tracking',
    description: 'Smart hour verification and performance tracking with AI-powered insights.',
    icon: Business,
  },
];

const studentJourneySteps = [
  {
    title: 'Create Profile',
    description: 'Sign up and tell us about your interests and career goals',
    icon: <Person />
  },
  {
    title: 'Discover Opportunities',
    description: 'Get AI-powered matches based on your interests',
    icon: <Psychology />
  },
  {
    title: 'Track Progress',
    description: 'Log hours and build your volunteer portfolio',
    icon: <Analytics />
  },
  {
    title: 'Gain Insights',
    description: 'Receive personalized skill development recommendations',
    icon: <School />
  }
];

const teacherJourneySteps = [
  {
    title: 'Setup Classroom',
    description: 'Create your digital classroom environment',
    icon: <School />
  },
  {
    title: 'Monitor Progress',
    description: 'Track student volunteer activities in real-time',
    icon: <TrackChanges />
  },
  {
    title: 'Verify Hours',
    description: 'Easily approve and validate student submissions',
    icon: <VerifiedUser />
  },
  {
    title: 'Generate Reports',
    description: 'Access comprehensive analytics and insights',
    icon: <Analytics />
  }
];

const employerJourneySteps = [
  {
    title: 'Create Organization',
    description: 'Set up your organization profile and team',
    icon: <Business />
  },
  {
    title: 'Post Opportunities',
    description: 'Create and manage volunteer positions',
    icon: <Work />
  },
  {
    title: 'Review Applications',
    description: 'Evaluate and select student volunteers',
    icon: <Assignment />
  },
  {
    title: 'Track Impact',
    description: 'Measure volunteer program effectiveness',
    icon: <Analytics />
  }
];

// Add keyframe animations
const float = keyframes`
  0% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(10deg);
  }
  100% {
    transform: translateY(0px) rotate(0deg);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.3;
  }
  100% {
    transform: scale(1);
    opacity: 0.5;
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const ComingSoonView = () => {
  const theme = useTheme();
  
  // Animation for the construction icon
  const float = keyframes`
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(10deg); }
  `;

  // Pulse animation for the background
  const pulse = keyframes`
    0%, 100% { transform: scale(1); opacity: 0.5; }
    50% { transform: scale(1.1); opacity: 0.3; }
  `;

  return (
    <PublicLayout hasDarkHero>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.95)} 0%, ${alpha(theme.palette.primary.main, 0.9)} 100%)`,
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '200px',
            background: `linear-gradient(to bottom, transparent, ${theme.palette.background.default})`,
            zIndex: 1,
          },
        }}
      >
        {/* Main content wrapper */}
        <Box
          sx={{
            minHeight: 'calc(100vh - 64px)', // Account for navbar height
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pb: '100px', // Add padding to account for gradient
          }}
        >
          {/* Animated background elements */}
          <Box
            sx={{
              position: 'absolute',
              top: '20%',
              left: '10%',
              width: '40vw',
              height: '40vw',
              borderRadius: '50%',
              background: `radial-gradient(circle at center, ${alpha(theme.palette.secondary.main, 0.2)} 0%, transparent 70%)`,
              animation: `${pulse} 6s ease-in-out infinite`,
              zIndex: 1,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '20%',
              right: '15%',
              width: '30vw',
              height: '30vw',
              borderRadius: '50%',
              background: `radial-gradient(circle at center, ${alpha(theme.palette.primary.light, 0.15)} 0%, transparent 70%)`,
              animation: `${pulse} 8s ease-in-out infinite 1s`,
              zIndex: 1,
            }}
          />

          {/* Additional decorative elements */}
          <Box
            sx={{
              position: 'absolute',
              top: '40%',
              right: '25%',
              width: '15vw',
              height: '15vw',
              borderRadius: '50%',
              background: `radial-gradient(circle at center, ${alpha(theme.palette.secondary.light, 0.1)} 0%, transparent 70%)`,
              animation: `${pulse} 7s ease-in-out infinite 0.5s`,
              zIndex: 1,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '40%',
              left: '25%',
              width: '20vw',
              height: '20vw',
              borderRadius: '50%',
              background: `radial-gradient(circle at center, ${alpha(theme.palette.primary.light, 0.08)} 0%, transparent 70%)`,
              animation: `${pulse} 9s ease-in-out infinite 1.5s`,
              zIndex: 1,
            }}
          />

          <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2, py: { xs: 8, md: 12 } }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Box
                sx={{
                  textAlign: 'center',
                  px: 3,
                }}
              >
                <Construction
                  sx={{
                    fontSize: { xs: 80, md: 120 },
                    color: 'white',
                    mb: 4,
                    animation: `${float} 3s ease-in-out infinite`,
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))',
                  }}
                />
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '4rem' },
                    fontWeight: 800,
                    color: 'white',
                    mb: 2,
                    textShadow: '0 4px 8px rgba(0,0,0,0.2)',
                    letterSpacing: '-0.02em',
                  }}
                >
                  Coming Soon
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: 'rgba(255,255,255,0.9)',
                    mb: 4,
                    maxWidth: '600px',
                    mx: 'auto',
                    lineHeight: 1.6,
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  We're building something amazing! Our comprehensive guide on how Dreamz Connect works will be available soon.
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={() => window.history.back()}
                    sx={{
                      bgcolor: 'white',
                      color: theme.palette.primary.main,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.common.white, 0.9),
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[4],
                      },
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      textTransform: 'none',
                      transition: 'all 0.2s ease-in-out',
                      boxShadow: theme.shadows[2],
                    }}
                  >
                    Go Back
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => window.location.href = '/contact'}
                    sx={{
                      borderColor: 'white',
                      color: 'white',
                      '&:hover': {
                        borderColor: alpha(theme.palette.common.white, 0.9),
                        bgcolor: alpha(theme.palette.common.white, 0.1),
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.common.white, 0.2)}`,
                      },
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      textTransform: 'none',
                      transition: 'all 0.2s ease-in-out',
                      backdropFilter: 'blur(4px)',
                    }}
                  >
                    Contact Us
                  </Button>
                </Box>
              </Box>
            </motion.div>
          </Container>
        </Box>
      </Box>
    </PublicLayout>
  );
};

const HowItWorksPage = () => {
  const featureFlags = getFeatureFlags();
  
  if (!featureFlags.enableHowItWorks) {
    return <ComingSoonView />;
  }

  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabValue>('students');
  const [animationDirection, setAnimationDirection] = useState<'left' | 'right'>('left');

  // Handle initial hash and hash changes
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (['students', 'teachers', 'employers'].includes(hash)) {
      setAnimationDirection(getAnimationDirection(activeTab, hash as TabValue));
      setActiveTab(hash as TabValue);
    }
  }, [location.hash]);

  const getAnimationDirection = (current: TabValue, next: TabValue) => {
    const order = ['students', 'teachers', 'employers'];
    const currentIndex = order.indexOf(current);
    const nextIndex = order.indexOf(next);
    return nextIndex > currentIndex ? 'left' : 'right';
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: TabValue) => {
    setAnimationDirection(getAnimationDirection(activeTab, newValue));
    setActiveTab(newValue);
    // Update URL hash without triggering navigation
    window.history.replaceState(null, '', `#${newValue}`);
  };

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/contact');
    window.scrollTo(0, 0);
  };

  const renderSteps = (steps: typeof studentSteps) => (
    <Grid container spacing={4} sx={{ mt: 4 }}>
      {steps.map((step, index) => (
        <Slide
          direction={animationDirection === 'left' ? 'right' : 'left'}
          in
          mountOnEnter
          unmountOnExit
          timeout={300 + index * 100}
          key={step.label}
        >
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[4],
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                    }}
                  >
                    <step.icon sx={{ color: theme.palette.primary.main }} />
                  </Box>
                  <Typography variant="h6">{step.label}</Typography>
                </Box>
                <Typography color="text.secondary">{step.description}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Slide>
      ))}
    </Grid>
  );

  const getActiveSteps = () => {
    switch (activeTab) {
      case 'students':
        return studentSteps;
      case 'teachers':
        return teacherSteps;
      case 'employers':
        return employerSteps;
      default:
        return studentSteps;
    }
  };

  const getTabTitle = () => {
    switch (activeTab) {
      case 'students':
        return 'For Students';
      case 'teachers':
        return 'For Teachers';
      case 'employers':
        return 'For Employers';
      default:
        return '';
    }
  };

  const getTabDescription = () => {
    switch (activeTab) {
      case 'students':
        return 'Discover meaningful volunteer opportunities that align with your interests and career goals.';
      case 'teachers':
        return 'Streamline the process of managing and verifying student volunteer hours.';
      case 'employers':
        return 'Connect with motivated students and manage your volunteer program efficiently.';
      default:
        return '';
    }
  };

  const getJourneySteps = () => {
    switch (activeTab) {
      case 'students':
        return studentJourneySteps;
      case 'teachers':
        return teacherJourneySteps;
      case 'employers':
        return employerJourneySteps;
      default:
        return studentJourneySteps;
    }
  };

  const renderJourneyFlow = () => (
    <Box 
      component="section"
      id="journey-flow-section"
      sx={{
        width: '100%',
        py: { xs: 8, md: 12 },
        background: theme.palette.background.default,
        position: 'relative',
        zIndex: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: theme.palette.divider,
        },
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h4"
          align="center"
          sx={{
            mb: 6,
            fontWeight: 600,
            color: theme.palette.text.primary,
            position: 'relative',
          }}
        >
          Your {activeTab === 'employers' ? 'Organization\'s' : ''} Journey with Dreamz Connect
        </Typography>
        <Timeline
          sx={{
            p: 0,
            [`& .MuiTimelineItem-root`]: {
              minHeight: 160,
            },
            [`& .MuiTimelineItem-missingOppositeContent`]: {
              '&:before': {
                display: 'none',
              }
            }
          }}
        >
          {getJourneySteps().map((step, index) => (
            <Fade in timeout={300 + index * 200} key={index}>
              <TimelineItem>
                <TimelineSeparator>
                  <TimelineDot 
                    sx={{ 
                      bgcolor: theme.palette.primary.main,
                      p: 1.5,
                      boxShadow: theme.shadows[3],
                    }}
                  >
                    {step.icon}
                  </TimelineDot>
                  {index < getJourneySteps().length - 1 && (
                    <TimelineConnector 
                      sx={{ 
                        bgcolor: theme.palette.primary.main,
                        width: 3,
                      }} 
                    />
                  )}
                </TimelineSeparator>
                <TimelineContent>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      bgcolor: alpha(theme.palette.background.paper, 0.9),
                      borderRadius: 2,
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        bgcolor: theme.palette.background.paper,
                        boxShadow: theme.shadows[8],
                      }
                    }}
                  >
                    <Typography variant="h6" component="h3" gutterBottom>
                      {step.title}
                    </Typography>
                    <Typography color="textSecondary">
                      {step.description}
                    </Typography>
                  </Paper>
                </TimelineContent>
              </TimelineItem>
            </Fade>
          ))}
        </Timeline>
      </Container>
    </Box>
  );

  return (
    <PublicLayout>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minHeight: '100vh',
          mt: { xs: '-64px', sm: '-72px' }, // Compensate for AppBar padding
        }}
      >
        {/* Hero Section */}
        <Box
          sx={{
            width: '100%',
            pt: { xs: 'calc(64px + 4rem)', sm: 'calc(72px + 4rem)' },
            pb: { xs: 2, sm: 3 },
            background: `linear-gradient(to bottom right, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.1)})`,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Container maxWidth="xl">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Fade in timeout={500}>
                  <Box>
                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: '2.5rem', md: '3.5rem' },
                        fontWeight: 800,
                        mb: 2,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        color: 'transparent',
                      }}
                    >
                      Transform Volunteer Management with AI
                    </Typography>
                    <Typography
                      variant="h5"
                      color="text.secondary"
                      sx={{ mb: 4, maxWidth: 600 }}
                    >
                      Discover how our AI-powered platform connects students, educators, and organizations to create meaningful volunteer experiences.
                    </Typography>
                  </Box>
                </Fade>
              </Grid>
              <Grid item xs={12} md={6}>
                <Fade in timeout={800}>
                  <Box sx={{ 
                    position: 'relative',
                    height: { xs: '300px', md: '400px' },
                    width: '100%',
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}>
                    {/* Animated Background Elements */}
                    <Box sx={{
                      position: 'absolute',
                      inset: 0,
                      opacity: 0.1,
                      background: `radial-gradient(circle at 30% 30%, ${theme.palette.primary.main} 0%, transparent 70%)`,
                      animation: `${pulse} 8s ease-in-out infinite`,
                    }} />
                    <Box sx={{
                      position: 'absolute',
                      inset: 0,
                      opacity: 0.05,
                      background: `radial-gradient(circle at 70% 70%, ${theme.palette.secondary.main} 0%, transparent 70%)`,
                      animation: `${pulse} 8s ease-in-out infinite 4s`,
                    }} />
                    
                    {/* Rotating Ring */}
                    <Box sx={{
                      position: 'absolute',
                      width: '300px',
                      height: '300px',
                      borderRadius: '50%',
                      border: `2px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
                      animation: `${rotate} 20s linear infinite`,
                    }} />

                    {/* Floating Icons */}
                    {[School, Psychology, Analytics, Work, Star].map((Icon, index) => (
                      <Box
                        key={index}
                        sx={{
                          position: 'absolute',
                          top: `${20 + (index * 15)}%`,
                          left: `${20 + (index * 15)}%`,
                          animation: `${float} 6s ease-in-out infinite`,
                          animationDelay: `${index * 0.8}s`,
                          transform: `rotate(${index * 45}deg)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: 1,
                          borderRadius: '50%',
                          bgcolor: alpha(theme.palette.background.paper, 0.8),
                          boxShadow: theme.shadows[2],
                          backdropFilter: 'blur(8px)',
                        }}
                      >
                        <Icon
                          sx={{
                            fontSize: { xs: 24, md: 32 },
                            color: theme.palette.primary.main,
                            opacity: 0.8,
                          }}
                        />
                      </Box>
                    ))}

                    {/* Central Element */}
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100px',
                        height: '100px',
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        animation: `${pulse} 3s ease-in-out infinite`,
                        boxShadow: `0 0 40px ${alpha(theme.palette.primary.main, 0.2)}`,
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          inset: -10,
                          border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                          borderRadius: '50%',
                          animation: `${rotate} 10s linear infinite`,
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src={dreamzConnectLogo}
                        alt="Dreamz Connect Logo"
                        sx={{
                          width: 200,
                          height: 200,
                          objectFit: 'contain',
                        }}
                      />
                    </Box>
                  </Box>
                </Fade>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* User Type Sections */}
        <Box
          component="section"
          sx={{
            width: '100%',
            position: 'relative',
            zIndex: 3,
            py: { xs: 4, md: 6 },
            background: 'linear-gradient(180deg, #F7FAFC 0%, #EDF2F7 100%)',
          }}
        >
          <Container maxWidth="xl">
            <Typography
              variant="h2"
              align="center"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 700,
                mb: 6,
              }}
            >
              Choose Your Path
            </Typography>

            {/* Tabs Navigation */}
            <Paper
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: theme.shadows[2],
                mb: 6,
              }}
            >
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{
                  '& .MuiTab-root': {
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    textTransform: 'none',
                    py: 2,
                  },
                  '& .MuiTabs-flexContainer': {
                    justifyContent: 'space-between',
                  }
                }}
              >
                <Tab
                  label="For Students"
                  value="students"
                  href="#students"
                  sx={{ flex: 1 }}
                />
                <Tab
                  label="For Teachers"
                  value="teachers"
                  href="#teachers"
                  sx={{ flex: 1 }}
                />
                <Tab
                  label="For Employers"
                  value="employers"
                  href="#employers"
                  sx={{ flex: 1 }}
                />
              </Tabs>
            </Paper>

            {/* Content Section */}
            <Fade key={activeTab} in timeout={300}>
              <Box>
                <Typography
                  variant="h3"
                  align="center"
                  sx={{
                    fontSize: { xs: '1.75rem', md: '2.5rem' },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  {getTabTitle()}
                </Typography>
                <Typography
                  variant="h5"
                  align="center"
                  color="text.secondary"
                  sx={{ mb: 6, maxWidth: '800px', mx: 'auto' }}
                >
                  {getTabDescription()}
                </Typography>
                {renderSteps(getActiveSteps())}
              </Box>
            </Fade>
          </Container>
        </Box>

        {/* Journey Flow Section */}
        {renderJourneyFlow()}

        {/* CTA Section */}
        <Box
          component="section"
          sx={{
            width: '100%',
            position: 'relative',
            zIndex: 4,
            py: { xs: 8, md: 12 },
            bgcolor: 'background.paper',
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Container maxWidth="md">
            <Box
              sx={{
                textAlign: 'center',
                p: 4,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.primary.main, 0.03),
              }}
            >
              <Typography variant="h4" gutterBottom>
                Ready to Transform Volunteer Management?
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Contact us to learn more about how Dreamz Connect can help your organization.
              </Typography>
              <Button
                onClick={handleContactClick}
                variant="contained"
                size="large"
                startIcon={<ContactSupport />}
                sx={{
                  py: 1.5,
                  px: 4,
                  borderRadius: 2,
                  fontSize: '1.1rem',
                }}
              >
                Get in Touch
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>
    </PublicLayout>
  );
};

export default HowItWorksPage; 